import { Injectable, signal, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, switchMap, catchError, of, tap } from 'rxjs';
import { ApiClientService, ApiResponse } from '@core/services/api-client.service';
import { CustomerProfileService } from './customer-profile.service';
import { SearchHistoryService } from './search-history.service';
import { AuthService } from './auth-new.service';
import { 
  SearchResponse, 
  SearchCountResponse, 
  SearchState, 
  SearchParams, 
  SearchConfiguration,
  GlobalSearchResult
} from '@shared/interfaces/search.interfaces';

// Import additional interfaces for enhanced search
import { 
  SearchSuggestion, 
  RecentSearch 
} from '@shared/interfaces/search-suggestions.interfaces';

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private apiClient = inject(ApiClientService);
  private customerProfileService = inject(CustomerProfileService);
  private searchHistoryService = inject(SearchHistoryService);
  private authService = inject(AuthService);

  // Configuration
  private config: SearchConfiguration = {
    enableHistory: true,
    enableAnalytics: true,
    enableSuggestions: true,
    historyRetentionDays: 30,
    debounceDelay: 500,
    enterKeyDelay: 800
  };

  // Signals for reactive state management
  private searchStateSignal = signal<SearchState>({
    query: '',
    isLoading: false,
    results: [],
    totalResults: 0,
    hasSearched: false,
    error: null,
    isCommittedSearch: false
  });

  // BehaviorSubjects for complex state management
  private searchTermSubject = new BehaviorSubject<string>('');
  private searchDataSubject = new BehaviorSubject<any>({});
  private searchCountSubject = new BehaviorSubject<any>({});
  private searchStateSubject = new BehaviorSubject<SearchState>(this.searchStateSignal());
  private companySearchHistoryCache = new BehaviorSubject<any>(null);

  // Public observables
  searchTerm$ = this.searchTermSubject.asObservable();
  searchData$ = this.searchDataSubject.asObservable();
  searchCount$ = this.searchCountSubject.asObservable();
  searchState$ = this.searchStateSubject.asObservable();

  // Public signals (read-only)
  searchState = this.searchStateSignal.asReadonly();

  // When true, debounced api/Customer/search is skipped (e.g. when globalSearchV2 handles search)
  private skipDebouncedSearch = false;

  setSkipDebouncedSearch(skip: boolean): void {
    this.skipDebouncedSearch = skip;
  }

  // Debounced search stream
  private debouncedSearch$ = this.searchTermSubject.pipe(
    debounceTime(this.config.debounceDelay),
    distinctUntilChanged(),
    switchMap(term => this.performDebouncedSearch(term))
  );

  // Unsubscriber
  private destroy$ = new Subject<void>();

  constructor() {
    this.initializeSearchStreams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize search streams and subscriptions
   */
  private initializeSearchStreams(): void {
    // Subscribe to debounced search for real-time suggestions
    this.debouncedSearch$.subscribe({
      next: (result) => {
        if (
          result &&
          result.data &&
          typeof result.data === 'object' &&
          Array.isArray(result.data.data)
        ) {
          this.updateSearchState({
            isLoading: false,
            results: result.data.data,
            totalResults: result.data.total || 0,
            hasSearched: true,
            error: null
          });
        } else if (result !== null) {
          // Only clear results for an actual API response with no valid data.
          // null means the search was skipped (empty term or skipDebouncedSearch flag) —
          // do NOT clear results in that case or it wipes the search-result page.
          this.updateSearchState({
            isLoading: false,
            results: [],
            totalResults: 0,
            hasSearched: true,
            error: null
          });
        }
      },
      error: (error) => {
        this.updateSearchState({
          isLoading: false,
          error: error.message || 'Search failed'
        });
      }
    });
  }

  /**
   * Update search term (triggers debounced search)
   */
  updateSearchTerm(term: string): void {
    this.searchTermSubject.next(term);
    this.updateSearchState({ query: term, isCommittedSearch: false });
  }

  /**
   * Perform immediate search (on Enter key press)
   */
  async performImmediateSearch(searchTerm: string, orderBy: 'asc' | 'desc' = 'asc'): Promise<{
    success: boolean;
    result?: ApiResponse<SearchResponse>;
    error?: string;
    navigationAction?: 'single' | 'multiple' | 'none';
  }> {
    if (!searchTerm?.trim()) {
      return { success: false, error: 'Search term is required' };
    }

    try {
      this.updateSearchState({ isLoading: true, error: null, isCommittedSearch: true });

      const params: SearchParams = {
        search: searchTerm.trim(),
        page: '1',
        orderBy
      };

      // Make the search API call
      const response = await this.apiClient.get<SearchResponse>('api/Customer/search', {
        params: params,
        showLoading: false // We're handling loading state manually
      }).toPromise();

      // Check API response status — Status: false means "no record found", not an error
      if (!response?.Status) {
        this.updateSearchState({
          isLoading: false,
          query: searchTerm.trim(),
          results: [],
          totalResults: 0,
          hasSearched: true,
          error: null,
          isCommittedSearch: true
        });
        return { success: true, navigationAction: 'none' };
      }

      // Process successful response
      const customers   = response?.data?.data as any || [];
      const totalResults = response?.data?.total || 0;

      this.updateSearchState({
        isLoading: false,
        query: searchTerm.trim(),
        results: customers as any,
        totalResults: totalResults,
        hasSearched: true,
        error: null
      });

      // Store search data for other components
      this.setSearchData(response.data?.data);

      // Save search to history if enabled
      if (this.config.enableHistory) {
        this.saveSearchToHistory(searchTerm, 'customers', customers);
      }

      // Get search analytics if enabled
      if (this.config.enableAnalytics) {
        this.fetchSearchAnalytics(searchTerm);
      }

      // Determine navigation action
      let navigationAction: 'single' | 'multiple' | 'none' = 'none';
      
      if (customers.length  === 1) {
        // Single result - set customer context and navigate to profile
        this.customerProfileService.setCustomerFromSearchResult(customers[0]);
        navigationAction = 'single';
      } else if (customers.length > 1) {
        // Multiple results - navigate to search results page
        navigationAction = 'multiple';
      }

      return {
        success: true,
        result: response,
        navigationAction
      };

    } catch (error: any) {
      console.error('Search error:', error);
      
      this.updateSearchState({
        isLoading: false,
        error: error?.error?.message || error?.message || 'Search failed',
        hasSearched: true
      });

      return {
        success: false,
        error: error?.error?.message || error?.message || 'Search failed',
        navigationAction: 'none'
      };
    }
  }

  /**
   * Perform debounced search for suggestions
   */
  private performDebouncedSearch(term: string, orderBy: 'asc' | 'desc' = 'asc'): Observable<ApiResponse<SearchResponse> | null> {
    if (this.skipDebouncedSearch || !term?.trim()) {
      return of(null);
    }

    this.updateSearchState({ isLoading: true });

    const params: SearchParams = {
      search: term.trim(),
      page: '1',
      orderBy
    };

    return this.apiClient.get<SearchResponse>('api/Customer/search', {
      params: params,
      showLoading: false
    }).pipe(
      catchError(error => {
        console.error('Debounced search error:', error);
        return of(null);
      })
    );
  }

  /**
   * Save search to history
   */
  private async saveSearchToHistory(searchTerm: string, activeTab: string, items: any): Promise<void> {
    try {
      await this.searchHistoryService.saveSearchRecord(searchTerm, activeTab, items);
    } catch (error) {
      console.error('Error saving search to history:', error);
      // Don't throw - history failure shouldn't break search
    }
  }

  /**
   * Fetch search analytics/count
   */
  private async fetchSearchAnalytics(searchTerm: string): Promise<void> {
    try {
      const user = this.authService.user();
      if (!user?.userID) {
        console.warn('No user ID available for search analytics');
        return;
      }

      const params = {
        companyId: "UNITYCRM0021", // TODO: Make this dynamic based on current company
        userID: user.userID.toString(),
        search: searchTerm
      };

      const response = await this.apiClient.get<SearchCountResponse>('api/Customer/SearchCount', {
        params: params,
        showLoading: false
      }).toPromise();

      if (response?.data?.Status) {
        this.setSearchCount(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching search analytics:', error);
      // Don't throw - analytics failure shouldn't break search
    }
  }

  /**
   * Update search state
   */
  private updateSearchState(updates: Partial<SearchState>): void {
    const currentState = this.searchStateSignal();
    const newState = { ...currentState, ...updates };
    
    this.searchStateSignal.set(newState);
    this.searchStateSubject.next(newState);
  }

  /**
   * Set search data (for backward compatibility)
   */
  setSearchData(data: any): void {
    this.searchDataSubject.next(data);
  }

  /**
   * Set search count (for backward compatibility)
   */
  setSearchCount(data: any): void {
    this.searchCountSubject.next(data);
  }

  /**
   * Clear search state
   */
  clearSearch(): void {
    this.searchTermSubject.next('');
    this.updateSearchState({
      query: '',
      isLoading: false,
      results: [],
      totalResults: 0,
      hasSearched: false,
      error: null
    });
  }

  /**
   * Get current search query
   */
  getCurrentQuery(): string {
    return this.searchStateSignal().query;
  }

  /**
   * Check if search is in progress
   */
  isSearching(): boolean {
    return this.searchStateSignal().isLoading;
  }

  /**
   * Get recent search history formatted for suggestions
   */
  async getRecentSearches(limit: number = 5): Promise<RecentSearch[]> {
    try {
      const recent = await this.searchHistoryService.getRecentSearchRecords(limit);
      return recent.map(search => ({
        id: (search.id || Math.random()).toString(),
        query: search.searchTerm || search.query || '',
        type: (search.activeTab === 'customers' ? 'customer' : 
              search.activeTab === 'orders' ? 'order' : 
              search.activeTab === 'tickets' ? 'ticket' : 'customer') as 'customer' | 'order' | 'ticket',
        timestamp: new Date(search.timestamp),
        resultCount: search.totalCount || 0,
        metadata: search.metadata
      }));
    } catch (error) {
      console.error('Error getting recent searches:', error);
      return [];
    }
  }

  /**
   * Convert search results to suggestions format
   */
  convertResultsToSuggestions(results: any[]): SearchSuggestion[] {
    return results.map(result => ({
      id: result.CustomerID?.toString() || Math.random().toString(),
      type: 'customer' as const,
      title: `${result.FirstName || ''} ${result.LastName || ''}`.trim(),
      subtitle: result.Email || result.MDN || '',
      metadata: {
        customerId: result.CustomerID?.toString(),
        customerName: `${result.FirstName || ''} ${result.LastName || ''}`.trim(),
        email: result.Email,
        phone: result.MDN,
        status: result.AccountStatus
      }
    }));
  }

  /**
   * Get live suggestions for enhanced search input
   */
  getLiveSuggestions(): SearchSuggestion[] {
    const searchState = this.searchStateSignal();
    if (searchState.results && searchState.results.length > 0) {
      return this.convertResultsToSuggestions(searchState.results);
    }
    return [];
  }

  /**
   * Clear search history
   */
  async clearSearchHistory(): Promise<void> {
    try {
      await this.searchHistoryService.clearSearchHistory();
    } catch (error) {
      console.error('Error clearing search history:', error);
      throw error;
    }
  }

  /**
   * Get search configuration
   */
  getConfiguration(): SearchConfiguration {
    return { ...this.config };
  }

  /**
   * Update search configuration
   */
  updateConfiguration(updates: Partial<SearchConfiguration>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Global search across multiple entity types
   */
  async globalSearch(searchTerm: string, tab: string, options?: { signal?: AbortSignal; page?: number; perPage?: number; filter?: string; orderBy?: 'asc' | 'desc' }): Promise<any> {
    const data: any = {
      searchTerm: searchTerm,
      tab: tab,
      page: options?.page || 1,
      per_page: options?.perPage || 5,
      orderBy: options?.orderBy || 'asc'
    };

    // Add filter if provided
    if (options?.filter) {
      data.filter = options.filter;
    }

    try {
      const response = await this.apiClient.post('api/orders/globalSearchV2 ', data).toPromise();
      return response;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Search request was aborted.');
        throw error;
      } else {
        throw error;
      }
    }
  }

  /**
   * Search customer (direct search)
   */
  searchCustomer(body: any, orderBy: 'asc' | 'desc' = 'asc'): Promise<any> {
    try {
      return this.apiClient.get('api/Customer/search', {
        params: { ...body, orderBy },
        showLoading: false
      }).toPromise();
    } catch (err) {
      throw new Error(err as string);
    }
  }

  /**
   * Get app search count
   */
  async getAppSearchCount(body: any): Promise<any> {
    try {
      const user = this.authService.user();
      const data = {
        companyId: "UNITYCRM0021",
        userID: user?.userID,
        search: body
      };
      return await this.apiClient.get('api/Customer/SearchCount', {
        params: data,
        showLoading: false
      }).toPromise();
    } catch (err) {
      throw new Error(err as string);
    }
  }

  /**
   * Get company search history from server
   */
  getCompanySearchHistory(agentId: any, forceRefresh: boolean = false): Observable<any> {
    // Skip cache if forceRefresh is true
    if (!forceRefresh && this.companySearchHistoryCache.value) {
      return this.companySearchHistoryCache.asObservable();
    }

    const body = { agentId: agentId, perPage: 50 };
    return this.apiClient.post('api/orders/getGlobalSearchLog', body, {
      showLoading: false
    }).pipe(
      tap((data) => this.companySearchHistoryCache.next(data))
    );
  }

  /**
   * Get order types
   */
  async getOrderType(): Promise<any> {
    try {
      const user = this.authService.user();
      const data = {
        companyId: "UNITYCRM0021",
        userID: user?.userID,
      };
      return await this.apiClient.get('api/orders/orderTypes', {
        params: data,
        showLoading: false
      }).toPromise();
    } catch (err) {
      throw new Error(err as string);
    }
  }

  /**
   * Get account types
   */
  async getAccountType(): Promise<any> {
    try {
      const user = this.authService.user();
      const data = {
        companyId: "UNITYCRM0021",
        userID: user?.userID,
      };
      return await this.apiClient.get('api/Customer/getCustomerTypes', {
        params: data,
        showLoading: false
      }).toPromise();
    } catch (err) {
      throw new Error(err as string);
    }
  }

  /**
   * Get customer status
   */
  async getCustomerStatus(): Promise<any> {
    try {
      const user = this.authService.user();
      const data = {
        companyId: "UNITYCRM0021",
        userID: user?.userID,
        CustomerId: this.customerProfileService.getCustomerId()
      };
      return await this.apiClient.get('api/Customer/GetCustomerStatus', {
        params: data,
        showLoading: false
      }).toPromise();
    } catch (err) {
      throw new Error(err as string);
    }
  }

  /**
   * Advanced search with filters
   */
  async advancedSearch(params: any): Promise<SearchResponse> {
    // TODO: Implement in Phase 3
    throw new Error('Advanced search not yet implemented');
  }

  /**
   * Get search analytics data
   */
  async getSearchAnalytics(): Promise<any> {
    try {
      const user = this.authService.user();
      if (!user?.userID) {
        throw new Error('No user ID available');
      }

      return await this.searchHistoryService.getCompanySearchHistory(user.userID.toString());
    } catch (error) {
      console.error('Error getting search analytics:', error);
      throw error;
    }
  }

  /**
   * Advance order search (from V1)
   */
  async advanceOrderSearch(data: any, orderBy: 'asc' | 'desc' = 'asc'): Promise<any> {
    try {
      const user = this.authService.user();
      const { fromDate, toDate, ...filteredData } = data || {};
      const body = {
        companyId: "UNITYCRM0021",
        userID: user?.userID,
        orderBy,
        ...filteredData
      };

      return await this.apiClient.get('api/Search/Advance', {
        params: body,
        showLoading: false
      }).toPromise();
    } catch (err) {
      throw new Error(err as string);
    }
  }

  /**
   * Set advanced search data
   */
  private advanceSearchedDataSubject = new BehaviorSubject<any>(null);
  advanceSearchedData$ = this.advanceSearchedDataSubject.asObservable();

  setAdvanceSearchedData(data: any): void {
    this.advanceSearchedDataSubject.next(data);
  }

  /**
   * Set advanced search payload data
   */
  private advanceSearchedPayloadDataSubject = new BehaviorSubject<any>(null);
  advanceSearchedPayloadData$ = this.advanceSearchedPayloadDataSubject.asObservable();

  setAdvanceSearchedPayloadData(data: any): void {
    this.advanceSearchedPayloadDataSubject.next(data);
  }

  /**
   * Signal to open the advanced search panel (e.g. from search-result edit icon)
   */
  private openAdvancedSearchPanelSubject = new BehaviorSubject<boolean>(false);
  openAdvancedSearchPanel$ = this.openAdvancedSearchPanelSubject.asObservable();

  openAdvancedSearchPanel(): void {
    this.openAdvancedSearchPanelSubject.next(true);
  }

  resetOpenAdvancedSearchPanel(): void {
    this.openAdvancedSearchPanelSubject.next(false);
  }

  /**
   * Prefill request — only set when the edit icon in search-result is clicked.
   * Null means the panel was opened normally (no prefill).
   */
  private prefillRequestSubject = new BehaviorSubject<any>(null);
  prefillRequest$ = this.prefillRequestSubject.asObservable();

  setPrefillRequest(data: any): void {
    this.prefillRequestSubject.next(data);
  }
}
